import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Alert,
  Chip,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Divider,
  LinearProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { Refresh, Download, ExpandMore, ExpandLess, Replay } from '@mui/icons-material';
import { useRelatoriosDashService } from '../../../../../../use-cases/relatorios-dash';
import { useAppSelector, useAppDispatch } from '../../../../../../presentation/hooks/store-hooks';
import {
  setRelatorioConsumoAnalytic,
  setMeusRelatorios,
  setCarregandoRelatorios,
  setRelatorioExpandido,
  setPageMeusRelatorios,
  setRowsPerPageMeusRelatorios,
  setRelatorioSemArquivo,
  addReprocessandoId,
  removeReprocessandoId
} from '../../../../../../application/states/relatorios-dash';
import {
  IRelatorioConsumoAnalyticRequest,
  IRelatorioConsumoAnalyticSource,
  IRelatorioConsumoAnalyticFileItem,
  FileType,
} from '../../../../../../application/models/IRelatorioDash';
import { useClientesService } from '../../../../../../use-cases/clientes';
import { useProdutoService } from '../../../../../../use-cases/produto';
import { useTipoConsumoService } from '../../../../../../use-cases/tipo-consumo';
import { useUsuariosService } from '../../../../../../use-cases/usuarios';
import { IClienteTable, IProduto, IUsuarioTable, ITipoUsuario } from '../../../../../../application/models';
import { useFilaStatus } from '../../../../../hooks/useFilaStatus';
import { getMensagemDisponibilidadeRelatorios } from '../../../../../../utils';
import { formatarNumero } from '../../../../../../utils/formatters';

export const RelatoriosAnaliticos = () => {
  const [formData, setFormData] = useState<IRelatorioConsumoAnalyticRequest>({
    IDPRODUTO: undefined,
    IDCLIENTE: undefined,
    IDUSUARIO: undefined,
    IDUSUARIO_VENDEDOR: undefined,
    TIPOCONSUMO: undefined,
    DATAINICIO: undefined,
    DATAFIM: undefined,
    generateFiles: false,
    format: FileType.XLSX,
    customTitle: '',
    customSubtitle: '',
    includeTotal: false,
    totalLabel: '',
    sheetName: ''
  } as IRelatorioConsumoAnalyticRequest);

  // Estados para os seletores
  const [clienteSelecionado, setClienteSelecionado] = useState<IClienteTable | null>(null);
  const [produtoSelecionado, setProdutoSelecionado] = useState<IProduto | null>(null);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<IUsuarioTable | null>(null);
  const [tiposConsumoSelecionados, setTiposConsumoSelecionados] = useState<number[]>([]);
  const [dataInicio, setDataInicio] = useState<Date | null>(null);
  const [dataFim, setDataFim] = useState<Date | null>(null);

  // Estados para busca de clientes
  const [filtroBusca, setFiltroBusca] = useState('');
  const [clientesFiltrados, setClientesFiltrados] = useState<IClienteTable[]>([]);
  const [carregandoClientes, setCarregandoClientes] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [filaId, setFilaId] = useState<number | null>(null);
  const [dataInicioError, setDataInicioError] = useState<string | null>(null);
  const [dataFimError, setDataFimError] = useState<string | null>(null);
  const [downloadingFileIndex, setDownloadingFileIndex] = useState<number | null>(null);
  const [downloadingFilaId, setDownloadingFilaId] = useState<number | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const processadoSemArquivoCyclesRef = useRef<number>(0);
  const MAX_POLLING_CYCLES = 600; // 600 × 5s = 50 minutos
  const MAX_PROCESSADO_SEM_ARQUIVO_CYCLES = 480; // 480 × 5s = 40 minutos

  // Estados para exibição de dados
  const [dados, setDados] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const lastSearchReturnedEmptyRef = useRef(false);

  const {
    meusRelatorios,
    carregandoRelatorios,
    relatorioExpandido,
    pageMeusRelatorios,
    rowsPerPageMeusRelatorios,
    reprocessandoIds
  } = useAppSelector((state: any) => state.relatoriosDash);

  // Hook para monitorar status da fila (intervalo 15s para não sobrecarregar o backend enquanto o relatório é processado)
  const { status: filaStatus, isPolling: isPollingFila, error: filaError } = useFilaStatus(filaId, 15000);
  const lastFetchedClienteIdRef = useRef<number | null>(null);

  // Estados do Redux
  const { consumoAnalytic: relatorioConsumoAnalytic } = useAppSelector((state: any) => state.relatoriosDash);

  // Serviços
  const {
    getRelatorioConsumoAnalytic,
    calcularTotalQuantidadeRelatorio,
    getMeusRelatorios,
    downloadArquivoRelatorio,
    reprocessarRelatorioComPolling,
    temRelatoriosProcessando: verificarTemRelatoriosProcessando,
    verificarMudancasRelatorios,
    deveAtualizarListaRelatorios,
    filaIdAindaProcessando: verificarFilaIdAindaProcessando,
    filaIdProcessadoComArquivo: verificarFilaIdProcessadoComArquivo
  } = useRelatoriosDashService();
  const { setClientesPagination, buscarClientes: buscarClientesService } = useClientesService();
  const { setAllProdutosAndTipoConsumo } = useTipoConsumoService();
  const { setUsuarios, setClienteSelecionado: setClienteSelecionadoRedux, setResetClienteSelecionado } = useUsuariosService();

  // Estados do Redux
  const dispatch = useAppDispatch();
  const { produtosTipoConsumo } = useAppSelector((state: any) => state.produto);
  const { usuarios, clienteSelecionado: clienteRedux } = useAppSelector((state: any) => state.usuarios);
  const { currentUser } = useAppSelector((state: any) => state.user);

  // Useful variables - Identificação de tipo de usuário
  const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS;
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO;
  const isComercial = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL;
  const isSupervisorComercial = currentUser?.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL;
  const isCX = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE;
  const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE;
  // Só exibe o card quando há algo na fila (filaId) ou resultado processado síncrono (PROCESSADO)
  const relatorioConsumoAnalyticTyped = relatorioConsumoAnalytic as IRelatorioConsumoAnalyticSource | null;
  const hasActiveReport = filaId !== null || (relatorioConsumoAnalyticTyped?.status === 3 || relatorioConsumoAnalyticTyped?.status === 'PROCESSADO' || relatorioConsumoAnalyticTyped?.statusText === 'PROCESSADO');
  const reportSource: IRelatorioConsumoAnalyticSource | null = hasActiveReport
    ? (filaId !== null ? (filaStatus ?? relatorioConsumoAnalyticTyped) : relatorioConsumoAnalyticTyped)
    : null;


  const dadosRelatorio = reportSource?.data ?? [];

  const COLUNAS_RELATORIO_ANALITICO = ['Cliente', 'Produto', 'Variável', 'Quantidade'];

  const mapearRowParaColunasFiltradas = (row: Record<string, unknown>): Record<string, unknown> => ({
    'Cliente':
      row.RazaoSocial ?? row.NomeRazaoSocial ?? row.Nome ?? row.NomeCompleto ?? row.Cliente ?? row.LoginCliente ?? '',
    Produto: row.Produto ?? '',
    'Variável': row.TipoConsumo ?? row.Descricao ?? '',
    Quantidade: row.Quantidade ?? row.QuantidadeEntrada ?? row.QuantidadeSaida ?? row.QUANTIDADE_SAIDA ?? row.QUANTIDADESAIDA ?? row.QUANTIDADEENTRADA ?? ''
  });

  const dadosRelatorioFiltrados =
    dadosRelatorio.length > 0
      ? dadosRelatorio.map((row: Record<string, unknown>) => mapearRowParaColunasFiltradas(row))
      : [];

  const resetFormState = useCallback(() => {
    if (!isCliente) {
      setClienteSelecionado(null);
      setClientesFiltrados([]);
    }
    setProdutoSelecionado(null);
    setUsuarioSelecionado(null);
    setTiposConsumoSelecionados([]);
    setDataInicio(null);
    setDataFim(null);
    setFiltroBusca('');
    setDados([]);
    setFilaId(null);
    setPage(0);
    lastSearchReturnedEmptyRef.current = false;
    setFormData({
      IDPRODUTO: undefined,
      IDCLIENTE: isCliente ? (clienteSelecionado?.IdCliente || undefined) : undefined,
      IDUSUARIO: undefined,
      IDUSUARIO_VENDEDOR: undefined,
      TIPOCONSUMO: undefined,
      DATAINICIO: undefined,
      DATAFIM: undefined,
      generateFiles: false,
      format: FileType.XLSX,
      customTitle: '',
      customSubtitle: '',
      includeTotal: false,
      totalLabel: '',
      sheetName: ''
    } as IRelatorioConsumoAnalyticRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters estáveis
  }, [isCliente]);

  // Reinicia todo o state da página ao abrir (montar o componente)
  useEffect(() => {
    resetFormState();
    setDataInicioError(null);
    setDataFimError(null);
    setDownloadingFileIndex(null);
    setRowsPerPage(10);
    dispatch(setRelatorioConsumoAnalytic(null));
    dispatch(setRelatorioExpandido(null));
    dispatch(setPageMeusRelatorios(0));
  }, [dispatch, resetFormState]);

  useEffect(() => {
    const buscarMeusRelatorios = async () => {
      if (!currentUser?.IdUsuario) return;

      dispatch(setCarregandoRelatorios(true));
      try {
        const result = await getMeusRelatorios(90, { silent: true });
        if (result?.success && result?.data?.relatorios) {
          const relatorios = result.data.relatorios;
          dispatch(setMeusRelatorios(relatorios));
          // NOTA: NÃO definir filaId automaticamente aqui.
          // O filaId só é definido via handleSubmit (ação explícita do usuário).
          // Auto-detect causava exibição de dados de cliente errado ao entrar na página.
        }
      } catch (error) {
        console.error('Erro ao buscar meus relatórios:', error);
      } finally {
        dispatch(setCarregandoRelatorios(false));
      }
    };

    buscarMeusRelatorios();
  }, [currentUser, getMeusRelatorios, dispatch]);

  useEffect(() => {
    if (
      filaStatus?.statusText === 'PROCESSADO' &&
      filaStatus?.files &&
      filaStatus.files.length > 0 &&
      currentUser?.IdUsuario
    ) {
      getMeusRelatorios(90, { silent: true }).then((result) => {
        if (result?.success && result?.data?.relatorios) {
          dispatch(setMeusRelatorios(result.data.relatorios));
        }
      });
    }
  }, [filaStatus?.statusText, filaStatus?.files, currentUser?.IdUsuario, getMeusRelatorios, dispatch]);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingCyclesRef = useRef<number>(0);
  const reprocessandoIdRef = useRef<number | null>(null);

  useEffect(() => {
    const relatoriosAnaliticos = meusRelatorios.filter((r: any) => r.IdRelatorioTipo === 2);
    const mine = reprocessandoIds.find((id: number) => relatoriosAnaliticos.some((r: any) => r.IdRelatorioFila === id)) ?? null;
    reprocessandoIdRef.current = mine;
  }, [reprocessandoIds, meusRelatorios]);

  useEffect(() => {
    if (!currentUser?.IdUsuario) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollingCyclesRef.current = 0;
      processadoSemArquivoCyclesRef.current = 0;
      return;
    }

    const temRelatoriosProcessando = verificarTemRelatoriosProcessando(meusRelatorios);
    const temFilaIdAtivo = filaId !== null && filaId !== undefined;
    const temReprocessandoAtivo = reprocessandoIdRef.current !== null;

    if (!temRelatoriosProcessando && !temFilaIdAtivo && !temReprocessandoAtivo) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollingCyclesRef.current = 0;
      processadoSemArquivoCyclesRef.current = 0;
      return;
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (temRelatoriosProcessando) {
      pollingCyclesRef.current = 0;
      processadoSemArquivoCyclesRef.current = 0;
    }

    pollingIntervalRef.current = setInterval(async () => {
      // Timeout absoluto: para o polling após MAX_POLLING_CYCLES ciclos (~10 min)
      pollingCyclesRef.current += 1;
      if (pollingCyclesRef.current > MAX_POLLING_CYCLES) {
        console.warn(`[POLLING] Timeout máximo atingido (${MAX_POLLING_CYCLES} ciclos). Parando polling.`);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        pollingCyclesRef.current = 0;
        processadoSemArquivoCyclesRef.current = 0;
        setPollingTimedOut(true);
        setFilaId(null);
        return;
      }

      try {
        const result = await getMeusRelatorios(90, { silent: true });
        if (result?.success && result?.data?.relatorios) {
          const relatorios = result.data.relatorios;

          const relatoriosAtualizados = verificarMudancasRelatorios(relatorios, meusRelatorios);
          const deveAtualizar = deveAtualizarListaRelatorios(relatoriosAtualizados, meusRelatorios);
          const filaIdAindaProcessando = verificarFilaIdAindaProcessando(filaId, relatorios);

          if (deveAtualizar) {
            dispatch(setMeusRelatorios(relatorios));
          }

          const filaProcessadaComArquivo = filaId && verificarFilaIdProcessadoComArquivo(filaId, relatorios);
          if (filaProcessadaComArquivo) {
            setFilaId(null);
            processadoSemArquivoCyclesRef.current = 0;
          }

          // Detectar ciclos de PROCESSADO sem arquivo (provável falha do DB)
          if (filaId !== null && !filaProcessadaComArquivo) {
            const relFilaAtual = relatorios.find((r: any) => r.IdRelatorioFila === filaId);
            if (relFilaAtual?.statusText === 'PROCESSADO' && !relFilaAtual?.hasFiles) {
              processadoSemArquivoCyclesRef.current += 1;
              if (processadoSemArquivoCyclesRef.current >= MAX_PROCESSADO_SEM_ARQUIVO_CYCLES) {
                console.warn(`[POLLING] filaId=${filaId} está PROCESSADO sem arquivo há ${processadoSemArquivoCyclesRef.current} ciclos. Parando polling (provável falha de geração).`);
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
                pollingCyclesRef.current = 0;
                processadoSemArquivoCyclesRef.current = 0;
                setPollingTimedOut(true);
                setFilaId(null);
                return;
              }
            } else {
              processadoSemArquivoCyclesRef.current = 0;
            }
          }

          if (reprocessandoIdRef.current !== null) {
            const reprocessadoComArquivo = verificarFilaIdProcessadoComArquivo(reprocessandoIdRef.current, relatorios);
            const reprocessadoComErro = relatorios.find((r: any) => r.IdRelatorioFila === reprocessandoIdRef.current)?.statusText === 'ERRO';
            if (reprocessadoComArquivo || reprocessadoComErro) {
              dispatch(removeReprocessandoId(reprocessandoIdRef.current));
              reprocessandoIdRef.current = null;
              if (!deveAtualizar) dispatch(setMeusRelatorios(relatorios));
            }
          }

          const filaAguardandoArquivo = (filaId !== null && !verificarFilaIdProcessadoComArquivo(filaId, relatorios))
            || (reprocessandoIdRef.current !== null && !verificarFilaIdProcessadoComArquivo(reprocessandoIdRef.current, relatorios));
          if (!verificarTemRelatoriosProcessando(relatorios) && !filaIdAindaProcessando && !filaAguardandoArquivo) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            pollingCyclesRef.current = 0;
            processadoSemArquivoCyclesRef.current = 0;
            if (filaProcessadaComArquivo) {
              setFilaId(null);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao atualizar lista de relatórios:', error);
      }
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [currentUser, getMeusRelatorios, filaId, meusRelatorios, dispatch, reprocessandoIds]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (currentUser) {
      setAllProdutosAndTipoConsumo();

      setClientesPagination({
        limit: 50,
        page: 1
      });

      if (isCliente) {
        buscarClientes('');
      }
    }
    // buscarClientes omitido de propósito: não é memoizado; colocá-lo nas deps faria o efeito rodar a cada render
  }, [currentUser, setAllProdutosAndTipoConsumo, isCliente, setClientesPagination]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (isCliente && !clienteSelecionado) {
      if (clienteRedux) {
        setClienteSelecionado(clienteRedux as any);
        setClientesFiltrados([clienteRedux as any]);
      } else {
        buscarClientes('');
      }
    } else if (isCliente && clientesFiltrados.length > 0) {
      const clienteDoUsuario = clientesFiltrados.find((c: any) => Number(c.IdCliente) === Number(currentUser?.IdCliente)) || clientesFiltrados[0];
      if (clienteDoUsuario && (!clienteSelecionado || clienteSelecionado.IdCliente !== clienteDoUsuario.IdCliente)) {
        setClienteSelecionado(clienteDoUsuario);
      }
    }
  }, [isCliente, clientesFiltrados, clienteSelecionado, clienteRedux, currentUser?.IdCliente]);

  const buscarClientes = async (termoBusca: string) => {
    if (!currentUser) return;

    if (isCliente && clienteSelecionado && termoBusca === '') {
      return;
    }

    setCarregandoClientes(true);

    try {
      let searchIdVendedor = 0;
      let searchIdCliente = 0;

      if (isComercial) {
        searchIdVendedor = currentUser.IdUsuario;
      }

      if (isCliente) {
        searchIdCliente = currentUser.IdCliente;
      }

      const clientes = await buscarClientesService({
        termoBusca,
        IdCliente: searchIdCliente,
        IdUsuario: Number(currentUser.IdUsuario),
        IdVendedor: searchIdVendedor,
        page: 1,
        limit: 50
      });

      if (clientes && clientes.length > 0) {
        setClientesFiltrados(clientes);

        if (isCliente) {
          const clienteDoUsuario = clientes.find((c: any) => Number(c.IdCliente) === Number(currentUser?.IdCliente)) || clientes[0];
          if (clienteDoUsuario) {
            setClienteSelecionado(clienteDoUsuario);
          }
        }
      } else if (!isCliente) {
        setClientesFiltrados([]);
      }
    } catch (error) {
      if (!isCliente) {
        setClientesFiltrados([]);
      }
    } finally {
      setCarregandoClientes(false);
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    const timer = setTimeout(() => {
      if (filtroBusca.length >= 3 || filtroBusca.length === 0) {
        buscarClientes(filtroBusca);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [filtroBusca, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuscaInputChange = (event: any, newInputValue: string, reason: any) => {
    if (reason === 'input' || reason === 'clear') {
      setFiltroBusca(newInputValue);
    }
  };

  const isInitialLoading = !produtosTipoConsumo;

  useEffect(() => {
    if (!clienteSelecionado) {
      setResetClienteSelecionado();
      lastFetchedClienteIdRef.current = null;
      return;
    }
    const idCliente = clienteSelecionado.IdCliente;
    if (lastFetchedClienteIdRef.current === idCliente) return;
    lastFetchedClienteIdRef.current = idCliente;
    setClienteSelecionadoRedux({ cliente: clienteSelecionado });
    setUsuarios({ idCliente });
  }, [clienteSelecionado, setClienteSelecionadoRedux, setUsuarios, setResetClienteSelecionado]);

  useEffect(() => {
    const isValidDate = (date: Date | null): boolean => {
      return date instanceof Date && !isNaN(date.getTime());
    };

    setFormData(prev => ({
      ...prev, // Preserva TODOS os campos anteriores, incluindo generateFiles, format, etc.
      IDCLIENTE: clienteSelecionado?.IdCliente || undefined,
      RAZAO_SOCIAL_CLIENTE: clienteSelecionado?.RazaoSocial ?? undefined,
      IDPRODUTO: produtoSelecionado?.IdProduto || undefined,
      IDUSUARIO: usuarioSelecionado?.IdUsuario || undefined,
      IDUSUARIO_REQUISICAO: currentUser?.IdUsuario || undefined,
      TIPOCONSUMO: tiposConsumoSelecionados.length > 0 ? tiposConsumoSelecionados[0] : undefined,
      DATAINICIO: (dataInicio && isValidDate(dataInicio)) ? dataInicio.toISOString().split('T')[0] : undefined,
      DATAFIM: (dataFim && isValidDate(dataFim)) ? dataFim.toISOString().split('T')[0] : undefined
    }));
  }, [clienteSelecionado, produtoSelecionado, usuarioSelecionado, tiposConsumoSelecionados, dataInicio, dataFim, currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setFilaId(null);
    setPollingTimedOut(false);
    processadoSemArquivoCyclesRef.current = 0;
    pollingCyclesRef.current = 0;
    lastSearchReturnedEmptyRef.current = false;
    setIsLoadingData(true);

    try {
      const result = await getRelatorioConsumoAnalytic(formData);

      const hasFilaId = !!result?.data?.filaId;
      let hasData = false;

      if (result?.data?.data && Array.isArray(result.data.data)) {
        setDados(result.data.data);
        setPage(0);
        hasData = result.data.data.length > 0;
      } else if (result?.success && result?.data) {
        if (result.data.allData && Array.isArray(result.data.allData)) {
          setDados(result.data.allData);
          setPage(0);
          hasData = result.data.allData.length > 0;
        } else if (Array.isArray(result.data)) {
          setDados(result.data);
          setPage(0);
          hasData = result.data.length > 0;
        } else {
          setDados([]);
        }
      } else {
        setDados([]);
      }

      // Só marca como "vazio" se não houver dados e NÃO for um processo de fila
      lastSearchReturnedEmptyRef.current = !hasData && !hasFilaId;

      if (hasFilaId) {
        const newFilaId = result?.data?.filaId;
        setFilaId(newFilaId);
        try {
          const relatoriosResult = await getMeusRelatorios(90, { silent: true });
          if (relatoriosResult?.success && relatoriosResult?.data?.relatorios) {
            dispatch(setMeusRelatorios(relatoriosResult.data.relatorios));
          }
        } catch (error) {
          console.error('Erro ao atualizar lista de relatórios:', error);
        }
      }

    } catch (error) {
      setDados([]);
      lastSearchReturnedEmptyRef.current = false;
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (field: keyof IRelatorioConsumoAnalyticRequest, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePageMeusRelatorios = (event: unknown, newPage: number) => {
    dispatch(setPageMeusRelatorios(newPage));
  };

  const handleChangeRowsPerPageMeusRelatorios = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRowsPerPageMeusRelatorios(parseInt(event.target.value, 10)));
    dispatch(setPageMeusRelatorios(0));
  };

  const getTiposConsumoDoProduto = () => {

    if (!produtoSelecionado || !produtosTipoConsumo) {
      return [];
    }

    const produtoTipoConsumo = produtosTipoConsumo.find((p: any) => p?.produto?.IdProduto === produtoSelecionado.IdProduto);

    return produtoTipoConsumo?.tipoConsumo || [];
  };

  const getProdutosUnicos = () => {
    if (!produtosTipoConsumo) return [];
    const produtos = produtosTipoConsumo.map((p: any) => p?.produto).filter(Boolean);

    return produtos;
  };


  // Se for COMERCIAL, não renderizar nada (o menu já está oculto no ListItems.tsx)
  if (isComercial) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Relatório Analítico
        </Typography>

        {/* Informativo sobre disponibilidade de dados e permissões */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {getMensagemDisponibilidadeRelatorios()}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Permissões:</strong>{' '}
            {(isDemais || isTecnologiaDaInformacao) && 'Você pode gerar relatórios de todos os clientes.'}
            {isComercial && 'Você pode gerar relatórios apenas dos clientes da sua carteira.'}
            {(isSupervisorComercial || isCX) && 'Você pode gerar relatórios dos clientes comerciais (exceto vendedor Veles).'}
            {isCliente && 'Você pode gerar relatórios apenas da sua empresa.'}
          </Typography>
        </Alert>


        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filtros
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Seletor de Cliente */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={clientesFiltrados || []}
                    getOptionLabel={(option) => `${option?.RazaoSocial || ''} - ${option?.NomeFantasia || option?.LoginCliente || ''}`}
                    value={clienteSelecionado}
                    onChange={(event, newValue) => {
                      // Sempre limpar state global ao trocar/limpar cliente
                      setResetClienteSelecionado();
                      setClienteSelecionado(newValue);
                      setUsuarioSelecionado(null);
                    }}
                    onInputChange={handleBuscaInputChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          carregandoClientes
                            ? `Buscando clientes...`
                            : `Selecione um Cliente ${clientesFiltrados.length ? `(${formatarNumero(clientesFiltrados.length)} encontrado${clientesFiltrados.length > 1 ? 's' : ''})` : ''}`
                        }
                        required
                        disabled={isInitialLoading}
                        placeholder={filtroBusca ? `Digite pelo menos 3 caracteres para buscar...` : 'Digite para buscar por CNPJ, Razão Social, Login ou Nome Fantasia'}
                        helperText="Digite pelo menos 3 caracteres para buscar clientes"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {carregandoClientes ? (
                                <Typography variant="caption" color="primary">
                                  Buscando...
                                </Typography>
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.IdCliente === value?.IdCliente}
                    loading={carregandoClientes}
                    noOptionsText={
                      carregandoClientes
                        ? "Buscando clientes..."
                        : filtroBusca.length < 3 && filtroBusca.length > 0
                          ? "Digite pelo menos 3 caracteres para buscar"
                          : filtroBusca
                            ? `Nenhum cliente encontrado para "${filtroBusca}"`
                            : "Digite para buscar clientes"
                    }
                    filterOptions={(options) => options}
                    freeSolo={false}
                    selectOnFocus={false}
                    clearOnBlur={false}
                  />
                </Grid>

                {/* Seletor de Produto */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={getProdutosUnicos()}
                    getOptionLabel={(option) => {
                      return option?.Descricao || option?.DescricaoProduto || 'Produto sem nome';
                    }}
                    value={produtoSelecionado}
                    onChange={(event, newValue) => {
                      setProdutoSelecionado(newValue);
                      setTiposConsumoSelecionados([]);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Selecione um Produto ${getProdutosUnicos().length ? `(${formatarNumero(getProdutosUnicos().length)})` : '(Nenhum)'}`}
                        required
                        disabled={isInitialLoading}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.IdProduto === value?.IdProduto}
                    loading={isLoadingData}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">
                            {option?.Descricao || option?.DescricaoProduto || 'Produto sem nome'}
                          </Typography>
                          {option?.DescricaoProduto && option?.DescricaoProduto !== option?.Descricao && (
                            <Typography variant="caption" color="text.secondary">
                              {option.DescricaoProduto}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>

                {/* Seletor de Variáveis */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    multiple
                    options={getTiposConsumoDoProduto()}
                    getOptionLabel={(option) => option?.Descricao || 'Variável'}
                    value={tiposConsumoSelecionados.map(id =>
                      getTiposConsumoDoProduto().find((tipo: any) => tipo?.IdTipoConsumo === id)
                    ).filter(Boolean)}
                    onChange={(event, newValue) => {
                      const ids = newValue.map((tipo: any) => tipo?.IdTipoConsumo).filter(Boolean);
                      setTiposConsumoSelecionados(ids);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Variáveis ${getTiposConsumoDoProduto().length ? `(${formatarNumero(getTiposConsumoDoProduto().length)})` : '(Nenhum)'}`}
                        disabled={!produtoSelecionado || isLoadingData}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.IdTipoConsumo === value?.IdTipoConsumo}
                    disabled={!produtoSelecionado || isLoadingData}
                  />
                </Grid>

                {/* Seletor de Usuário */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={usuarios || []}
                    getOptionLabel={(option) => `${option?.Nome || ''} - ${option?.LoginUsuario || ''}`}
                    value={usuarioSelecionado}
                    onChange={(event, newValue) => {
                      setUsuarioSelecionado(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Selecione um Usuário ${usuarios?.length ? `(${formatarNumero(usuarios.length)})` : '(Nenhum)'}`}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.IdUsuario === value?.IdUsuario}
                    disabled={!clienteSelecionado || isLoadingData}
                  />
                </Grid>

                {/* Data Início */}
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Data Início *"
                    value={dataInicio}
                    onChange={(newValue) => setDataInicio(newValue)}
                    onError={(error) => {
                      if (error === 'minDate') {
                        setDataInicioError('Data não pode ser anterior a 3 meses atrás');
                      } else if (error === 'maxDate') {
                        setDataInicioError('Data não pode ser no futuro');
                      } else if (error === 'invalidDate') {
                        setDataInicioError('Data inválida');
                      } else {
                        setDataInicioError(null);
                      }
                    }}
                    minDate={(() => {
                      const dataLimite = new Date();
                      dataLimite.setMonth(dataLimite.getMonth() - 3);
                      dataLimite.setDate(1);
                      return dataLimite;
                    })()}
                    maxDate={new Date()}
                    disabled={isLoadingData}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!dataInicioError,
                        helperText: dataInicioError
                      }
                    }}
                  />
                </Grid>

                {/* Data Fim */}
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Data Fim *"
                    value={dataFim}
                    onChange={(newValue) => setDataFim(newValue)}
                    onError={(error) => {
                      if (error === 'minDate') {
                        setDataFimError('Data não pode ser anterior a 3 meses atrás');
                      } else if (error === 'maxDate') {
                        setDataFimError('Data não pode ser no futuro');
                      } else if (error === 'invalidDate') {
                        setDataFimError('Data inválida');
                      } else {
                        setDataFimError(null);
                      }
                    }}
                    minDate={(() => {
                      const dataLimite = new Date();
                      dataLimite.setMonth(dataLimite.getMonth() - 3);
                      dataLimite.setDate(1);
                      return dataLimite;
                    })()}
                    maxDate={new Date()}
                    disabled={isLoadingData}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!dataFimError,
                        helperText: dataFimError
                      }
                    }}
                  />
                </Grid>

                {/* Configurações de Arquivo */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Configurações de Arquivo
                  </Typography>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.generateFiles || false}
                        onChange={(e) => handleInputChange('generateFiles', e.target.checked)}
                        disabled={isLoadingData}
                      />
                    }
                    label="Gerar Arquivos"
                  />
                </Grid>

                {formData.generateFiles && (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Formato</InputLabel>
                        <Select
                          value={formData.format}
                          onChange={(e) => handleInputChange('format', e.target.value)}
                          disabled={isLoadingData}
                        >
                          <MenuItem value="xlsx">Excel</MenuItem>
                          <MenuItem value="csv">CSV</MenuItem>
                          <MenuItem value="txt">TXT</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* Botões */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoadingData || !clienteSelecionado || !produtoSelecionado || !dataInicio || !dataFim || !!dataInicioError || !!dataFimError}
                    startIcon={<Refresh />}
                  >
                    {isLoadingData ? 'Gerando...' : 'Gerar Relatório'}
                  </Button>
                  {/* <Button
                    type="button"
                    variant="outlined"
                    onClick={resetFormState}
                    disabled={isLoadingData}
                  >
                    Limpar
                  </Button> */}
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {reportSource && !filaId && !formData.generateFiles && (reportSource.statusText === 'PROCESSADO' || reportSource.status === 3 || reportSource.status === 'PROCESSADO') && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Relatório Analítico
              </Typography>


              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Chip
                  label={reportSource.statusText ?? reportSource.status}
                  color={
                    reportSource.statusText === 'PROCESSADO' || reportSource.status === 3 || reportSource.status === 'PROCESSADO' ? 'success' :
                      reportSource.statusText === 'ERRO' || reportSource.status === 4 || reportSource.status === 'ERRO' ? 'error' :
                        reportSource.statusText === 'EM_ANDAMENTO' || reportSource.status === 2 || reportSource.status === 'EM_ANDAMENTO' ? 'warning' :
                          'info'
                  }
                  variant="outlined"
                />
                {isPollingFila && (
                  <Chip label="Verificando..." color="primary" size="small" />
                )}
                {filaStatus?.files && filaStatus.files.length > 0 && (reportSource.statusText === 'PROCESSADO' || reportSource.status === 3 || reportSource.status === 'PROCESSADO') && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(() => {
                      // Se existe arquivo ZIP, mostra apenas o ZIP (pois ele contém todas as partes)
                      const hasZip = filaStatus.files.some((f: IRelatorioConsumoAnalyticFileItem) =>
                        f.type?.toLowerCase() === FileType.ZIP || f.filename?.toLowerCase().endsWith('.zip')
                      );

                      const filesToShow = hasZip
                        ? filaStatus.files.filter((f: IRelatorioConsumoAnalyticFileItem) =>
                          f.type?.toLowerCase() === FileType.ZIP || f.filename?.toLowerCase().endsWith('.zip')
                        )
                        : filaStatus.files;

                      return filesToShow.map((file: IRelatorioConsumoAnalyticFileItem, index: number) => (
                        <Button
                          key={`${filaId || 'result'}-${file.filename}`}
                          variant="outlined"
                          size="small"
                          startIcon={downloadingFileIndex === index ? undefined : <Download />}
                          disabled={downloadingFileIndex !== null}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (downloadingFileIndex !== null) return;

                            const downloadPromise = downloadArquivoRelatorio(file);
                            setDownloadingFileIndex(index);

                            try {
                              await downloadPromise;
                            } catch (error) {
                              console.error('Erro ao baixar arquivo:', error);
                            } finally {
                              setDownloadingFileIndex(null);
                            }
                          }}
                        >
                          {downloadingFileIndex === index ? 'Baixando...' : `Baixar ${file.type.toUpperCase()}`}
                        </Button>
                      ));
                    })()}
                  </Box>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {(reportSource.statusText !== 'PROCESSADO' && reportSource.status !== 3 && reportSource.status !== 'PROCESSADO') &&
                  reportSource.message}
              </Typography>

              {(reportSource.statusText === 'ERRO' || reportSource.status === 4 || reportSource.status === 'ERRO') && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {reportSource.Erro || reportSource.message || filaError || 'Erro no processamento'}
                </Alert>
              )}

              {(reportSource.statusText === 'PROCESSADO' || reportSource.status === 3 || reportSource.status === 'PROCESSADO') && (
                <>
                  {reportSource.message && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {reportSource.message}
                    </Alert>
                  )}
                  {dadosRelatorioFiltrados.length > 0 && (
                    <>
                      <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {COLUNAS_RELATORIO_ANALITICO.map((key) => (
                                <TableCell key={key} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                  {key}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dadosRelatorioFiltrados
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((row: Record<string, unknown>, index: number) => (
                                <TableRow key={index}>
                                  {COLUNAS_RELATORIO_ANALITICO.map((key) => {
                                    const value = row[key];
                                    return (
                                      <TableCell key={key}>
                                        {typeof value === 'boolean' ? (
                                          <Chip
                                            label={value ? 'Sim' : 'Não'}
                                            color={value ? 'success' : 'default'}
                                            size="small"
                                          />
                                        ) : (
                                          formatarNumero(
                                            typeof value === 'number' || typeof value === 'string'
                                              ? value
                                              : value == null
                                                ? ''
                                                : String(value)
                                          )
                                        )}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                          </TableBody>
                          {(() => {
                            const total = calcularTotalQuantidadeRelatorio(dadosRelatorioFiltrados);
                            if (total <= 0) return null;
                            return (
                              <TableFooter>
                                <TableRow>
                                  <TableCell colSpan={3} sx={{ borderTop: 1, borderColor: 'divider' }} />
                                  <TableCell
                                    sx={{
                                      borderTop: 1,
                                      borderColor: 'divider',
                                      fontWeight: 'bold',
                                      color: 'text.primary'
                                    }}
                                  >
                                    TOTAL: {formatarNumero(total)}
                                  </TableCell>
                                </TableRow>
                              </TableFooter>
                            );
                          })()}
                        </Table>
                      </TableContainer>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <TablePagination
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          component="div"
                          count={dadosRelatorioFiltrados.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          labelRowsPerPage="Registros por página:"
                          labelDisplayedRows={({ from, to, count }) => `${formatarNumero(from)}-${formatarNumero(to)} de ${formatarNumero(count)}`}
                        />
                      </Box>
                      {formData.includeTotal && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="body1" fontWeight="bold">
                            {formData.totalLabel || 'Total'}: {dadosRelatorioFiltrados.reduce((acc, row) => {
                              const quantidade = Number(row.Quantidade) || 0;
                              return acc + quantidade;
                            }, 0)}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                  {dadosRelatorioFiltrados.length === 0 && !(reportSource?.files && reportSource.files.length > 0) && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Nenhum dado encontrado para os filtros selecionados.
                    </Alert>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Lista de Meus Relatórios */}
        {(carregandoRelatorios || meusRelatorios.some((r: any) => r.IdRelatorioTipo === 2)) && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Meus Relatórios
              </Typography>

              {carregandoRelatorios ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <Typography>Carregando relatórios...</Typography>
                </Box>
              ) : meusRelatorios.filter((r: any) => r.IdRelatorioTipo === 2).length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Você ainda não possui relatórios gerados. Gere um relatório usando o formulário acima.
                  </Typography>
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: 50 }}>Detalhes</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Nome Extração</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Data de Criação</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Download</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {meusRelatorios
                          .filter((rel: any) => rel.IdRelatorioTipo === 2)
                          .slice(pageMeusRelatorios * rowsPerPageMeusRelatorios, pageMeusRelatorios * rowsPerPageMeusRelatorios + rowsPerPageMeusRelatorios)
                          .map((relatorio: any) => {
                            const isExpanded = relatorioExpandido === relatorio.IdRelatorioFila;
                            const dataCriacao = new Date(relatorio.DataCriacao);
                            const dataFormatada = dataCriacao.toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            });

                            return (
                              <React.Fragment key={relatorio.IdRelatorioFila}>
                                <TableRow>
                                  <TableCell>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        dispatch(setRelatorioExpandido(isExpanded ? null : relatorio.IdRelatorioFila));
                                      }}
                                      sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                    </Button>
                                  </TableCell>
                                  <TableCell>{relatorio.NomeExtracao || `Relatório ${relatorio.IdRelatorioFila}`}</TableCell>
                                  <TableCell>{relatorio.Cliente || '-'}</TableCell>
                                  <TableCell>{dataFormatada}</TableCell>
                                  <TableCell>
                                    {relatorio.statusText === 'EM_FILA' || relatorio.statusText === 'EM_ANDAMENTO' || reprocessandoIds.includes(relatorio.IdRelatorioFila) || (
                                      relatorio.statusText === 'PROCESSADO' &&
                                      relatorio.IdRelatorioFila === filaId &&
                                      !(relatorio.hasFiles && relatorio.files?.length) &&
                                      !(filaStatus?.files && filaStatus.files.length > 0)
                                    ) ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinearProgress sx={{ width: 100, height: 6, borderRadius: 1 }} />
                                        <Typography variant="caption" color="text.secondary">
                                          Processando...
                                        </Typography>
                                      </Box>
                                    ) : relatorio.statusText === 'PROCESSADO' && (
                                      (relatorio.hasFiles && relatorio.files && relatorio.files.length > 0) ||
                                      (relatorio.IdRelatorioFila === filaId && filaStatus?.files && filaStatus.files.length > 0)
                                    ) ? (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={downloadingFilaId === relatorio.IdRelatorioFila ? null : <Download />}
                                        disabled={downloadingFilaId !== null}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={async (e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          if (downloadingFilaId !== null) return;

                                          // Prioriza o arquivo ZIP se existir
                                          const filesList = (relatorio.files && relatorio.files.length > 0)
                                            ? relatorio.files
                                            : (relatorio.IdRelatorioFila === filaId && filaStatus?.files)
                                              ? filaStatus.files
                                              : [];

                                          const zipFile = filesList.find((f: any) =>
                                            f.type?.toLowerCase() === FileType.ZIP || f.filename?.toLowerCase().endsWith('.zip')
                                          );

                                          const arquivoParaDownload = zipFile || filesList[0];

                                          if (!arquivoParaDownload) return;

                                          const downloadPromise = downloadArquivoRelatorio(
                                            arquivoParaDownload,
                                            (relatorios) => {
                                              dispatch(setMeusRelatorios(relatorios))
                                            },
                                            () => {
                                              if (relatorio.IdRelatorioFila === filaId) {
                                                setFilaId(null)
                                              }
                                              dispatch(setRelatorioSemArquivo(relatorio.IdRelatorioFila))
                                            }
                                          );

                                          setDownloadingFilaId(relatorio.IdRelatorioFila);

                                          try {
                                            await downloadPromise;
                                          } catch (error: any) {
                                            try {
                                              const result = await getMeusRelatorios(90, { silent: true })
                                              if (result?.success && result?.data?.relatorios) {
                                                dispatch(setMeusRelatorios(result.data.relatorios))
                                              }
                                            } catch (updateError) {
                                              console.error('Erro ao atualizar lista após falha no download:', updateError)
                                            }
                                          } finally {
                                            setDownloadingFilaId(null);
                                          }
                                        }}
                                        sx={{ color: '#9c27b0' }}
                                      >
                                        {downloadingFilaId === relatorio.IdRelatorioFila ? 'Baixando...' : 'Baixar'}
                                      </Button>
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color={relatorio.statusText === 'ERRO' ? 'error' : 'primary'}
                                        startIcon={<Replay />}
                                        onClick={async () => {
                                          dispatch(addReprocessandoId(relatorio.IdRelatorioFila));
                                          setFilaId(null);
                                          try {
                                            pollingCyclesRef.current = 0;
                                            await reprocessarRelatorioComPolling(
                                              relatorio.IdRelatorioFila,
                                              (relatorios) => {
                                                const atualizado = filaId !== null && filaStatus?.files?.length
                                                  ? relatorios.map((r: any) =>
                                                    r.IdRelatorioFila === filaId
                                                      ? { ...r, hasFiles: true, files: filaStatus.files }
                                                      : r
                                                  )
                                                  : relatorios;
                                                dispatch(setMeusRelatorios(atualizado));
                                              },
                                              (novaFilaId) => {
                                                setFilaId(novaFilaId);
                                              }
                                            );
                                          } catch (error) {
                                            dispatch(removeReprocessandoId(relatorio.IdRelatorioFila));
                                            throw error;
                                          }
                                        }}
                                      >
                                        Reprocessar
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow>
                                    <TableCell colSpan={5} sx={{ py: 2, backgroundColor: 'grey.50' }}>
                                      <Box sx={{ pl: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Detalhes do Relatório
                                        </Typography>
                                        <Grid container spacing={2}>
                                          <Grid item xs={12} md={6}>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Tipo de Arquivo:</strong> {
                                                relatorio.files && relatorio.files.length > 0
                                                  ? Array.from(new Set(relatorio.files.map((file: any) => {
                                                    const tipo = file.type?.toLowerCase() || '';
                                                    if (tipo === FileType.XLSX || file.filename?.endsWith('.xlsx')) return 'Excel';
                                                    if (tipo === FileType.CSV || file.filename?.endsWith('.csv')) return 'CSV';
                                                    if (tipo === FileType.TXT || file.filename?.endsWith('.txt')) return 'TXT';
                                                    if (tipo === FileType.ZIP || file.filename?.endsWith('.zip')) return 'ZIP';
                                                    return 'Desconhecido';
                                                  }))).join(', ')
                                                  : (() => {
                                                    const fmt = relatorio.format?.toLowerCase();
                                                    if (fmt === FileType.XLSX) return 'Excel';
                                                    if (fmt === FileType.CSV) return 'CSV';
                                                    if (fmt === FileType.TXT) return 'TXT';
                                                    return 'N/A';
                                                  })()
                                              }
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Tipo de Relatório:</strong> {relatorio.IdRelatorioTipo === 1 ? 'Acesso' : relatorio.IdRelatorioTipo === 2 ? 'Consumo Analítico' : 'Desconhecido'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Status:</strong> {
                                                relatorio.statusText === 'EM_FILA' ? 'Em Fila' :
                                                  relatorio.statusText === 'EM_ANDAMENTO' ? 'Em Andamento' :
                                                    relatorio.statusText === 'PROCESSADO' ? 'Processado' :
                                                      relatorio.statusText === 'ERRO' ? 'Erro' :
                                                        relatorio.statusText || 'Desconhecido'
                                              }
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} md={6}>
                                            {relatorio.Erro && (
                                              <Alert severity="error" sx={{ mt: 1 }}>
                                                <Typography variant="body2">
                                                  <strong>Erro:</strong> {relatorio.Erro}
                                                </Typography>
                                              </Alert>
                                            )}
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      component="div"
                      count={meusRelatorios.filter((rel: any) => rel.IdRelatorioTipo === 2).length}
                      rowsPerPage={rowsPerPageMeusRelatorios}
                      page={pageMeusRelatorios}
                      onPageChange={handleChangePageMeusRelatorios}
                      onRowsPerPageChange={handleChangeRowsPerPageMeusRelatorios}
                      labelRowsPerPage="Registros por página:"
                      labelDisplayedRows={({ from, to, count }) => `${formatarNumero(from)}-${formatarNumero(to)} de ${formatarNumero(count)}`}
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Alert de timeout do polling */}
        {pollingTimedOut && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Tempo limite de processamento atingido.</strong> O relatório demorou mais do que o esperado para ser gerado.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Isso pode ter ocorrido por instabilidade no banco de dados. Verifique a lista "Meus Relatórios" abaixo ou tente gerar novamente.
            </Typography>
          </Alert>
        )}

        {/* Alert só quando o backend respondeu e retornou vazio (não ao apenas selecionar filtros) */}
        {!isLoadingData && !filaId && !filaStatus && lastSearchReturnedEmptyRef.current && dados.length === 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Nenhum registro encontrado</strong> para os filtros selecionados.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Tente ajustar os filtros ou o período de datas.
            </Typography>
          </Alert>
        )}

      </Box>
    </LocalizationProvider>
  );
};
